import { useEffect, useState } from "react";
import { getProductSources, unlinkSource } from "../../../network/service/NutritionService";
import { SOURCE_NAMES } from "./nutrition-model";

const KIND_TEXT = { BARCODE: "barcode match", ADMIN_PICK: "chosen by an admin", NAME_MATCH: "name match" };

const sourceName = (source) => SOURCE_NAMES[source] ?? source;

/**
 * External records linked to a product, the value per field from each, which
 * one the product uses and where they disagree. `onChanged` runs after a
 * record is unlinked (the values may have been merged again).
 */
function SourcesPanel({ productId, canEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getProductSources(productId)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const onUnlink = async (s) => {
    if (!window.confirm(`Stop using "${s.name}" (${sourceName(s.source)}) for this product?`)) return;
    setBusy(s.recordId);
    try {
      setData(await unlinkSource(productId, s.recordId));
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (error) return <p className="text-danger">{error}</p>;
  if (!data) return <p className="nutri-muted">Loading sources…</p>;

  const recordName = (recordId) => data.sources.find((s) => s.recordId === recordId)?.name;
  const origin = (from) =>
    !from ? "—" : from.recordId ? `${sourceName(from.source)}` : `${sourceName(from.source)} (typed)`;

  return (
    <div className="nutri-card">
      {data.conflicts.length > 0 ? (
        <p className="nutri-missing">
          Sources disagree on {data.conflicts.map((c) => c.name.toLowerCase()).join(", ")}. Check the pack, then
          correct the value or unlink the wrong record.
        </p>
      ) : (
        data.sources.length > 1 && <p className="nutri-muted">All linked sources agree.</p>
      )}

      {data.sources.length === 0 ? (
        <p className="nutri-muted">
          No database records are linked yet. Auto-fill or pick a match to compare the label with Open Food Facts,
          USDA or FatSecret.
        </p>
      ) : (
        <table className="table table-sm nutri-sources">
          <thead>
            <tr>
              <th>Per 100 g</th>
              <th>Product</th>
              {data.sources.map((s) => (
                <th key={s.recordId} title={`${s.name}${s.brand ? ` · ${s.brand}` : ""}`}>
                  {sourceName(s.source)}
                  <div className="nutri-muted">{KIND_TEXT[s.kind]}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.fields
              .filter((f) => f.value !== null || Object.values(f.bySource).some((v) => v !== null))
              .map((f) => (
                <tr key={f.field} className={f.conflict ? "table-warning" : ""}>
                  <td>{f.name}</td>
                  <td title={f.from?.recordId ? recordName(f.from.recordId) : undefined}>
                    <strong>{f.value ?? "—"}</strong> {f.value !== null && f.unit}
                    <div className="nutri-muted">{origin(f.from)}</div>
                  </td>
                  {data.sources.map((s) => {
                    const v = f.bySource[s.recordId];
                    const used = f.from?.recordId === s.recordId;
                    return (
                      <td key={s.recordId} className={used ? "fw-bold" : ""}>
                        {v ?? "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
          {canEdit && (
            <tfoot>
              <tr>
                <td colSpan={2} className="nutri-muted">Linked records</td>
                {data.sources.map((s) => (
                  <td key={s.recordId}>
                    <button
                      type="button"
                      className="nutri-link"
                      disabled={busy === s.recordId}
                      onClick={() => onUnlink(s)}
                    >
                      {busy === s.recordId ? "…" : "Unlink"}
                    </button>
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      )}
      <small className="nutri-muted">
        Values are taken from the most trusted source: pack label, then Open Food Facts by barcode, ICMR-NIN, USDA,
        Open Food Facts by name and FatSecret. Energy and macros always come from one source together.
      </small>
    </div>
  );
}

export default SourcesPanel;
